// src/components/UploadedDocumentsViewer.jsx - ADAPTED TO MATCH ContractPDFViewer PATTERN
import React, { useState, useEffect } from 'react';
import {
  FileText,
  Image as ImageIcon,
  Archive,
  File,
  Download,
  Eye,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
  Minimize2
} from 'lucide-react';
import toast from 'react-hot-toast';

const UploadedDocumentsViewer = ({ token, autoRefresh = false }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  
  // Preview controls (similar to ContractPDFViewer)
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, [token]);

  // Cleanup blob URL on unmount or when preview changes
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const loadDocuments = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const { contractAccessService } = await import('../../services/contractAccessService');
      const data = await contractAccessService.getSignedDocuments(token);
      
      console.log('Loaded documents:', data);
      
      if (data && data.files) {
        setDocuments(data.files);
      } else if (Array.isArray(data)) {
        setDocuments(data);
      } else {
        setDocuments([]);
      }
    } catch (err) {
      console.error('Error loading documents:', err);
      // If 404, it means no documents uploaded yet (not an error)
      if (err.response?.status === 404) {
        setDocuments([]);
      } else {
        setError('Eroare la încărcarea documentelor');
      }
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.toLowerCase().split('.').pop();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return <ImageIcon className="w-5 h-5 text-blue-500" />;
    }
    if (['zip', '7z', 'rar'].includes(ext)) {
      return <Archive className="w-5 h-5 text-orange-500" />;
    }
    if (['pdf'].includes(ext)) {
      return <FileText className="w-5 h-5 text-red-500" />;
    }
    if (['doc', 'docx'].includes(ext)) {
      return <FileText className="w-5 h-5 text-blue-600" />;
    }
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const isImage = (fileName) => {
    const ext = fileName.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  };

  const isPDF = (fileName) => {
    const ext = fileName.toLowerCase().split('.').pop();
    return ext === 'pdf';
  };

  const canPreview = (fileName) => {
    return isImage(fileName) || isPDF(fileName);
  };

  const handleDownload = async (file) => {
    try {
      console.log('Downloading file:', file);
      
      // Construct download URL (public access, no auth needed)
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5076/api';
      const downloadUrl = `${baseUrl}/contract-access/${token}/download-file?path=${encodeURIComponent(file.filePath)}`;
      
      console.log('Download URL:', downloadUrl);
      
      // Fetch file as blob
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Accept': '*/*',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = file.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      URL.revokeObjectURL(url);
      
      toast.success('Fișier descărcat cu succes');
    } catch (err) {
      console.error('Error downloading file:', err);
      toast.error('Eroare la descărcarea fișierului');
    }
  };

  const handlePreview = async (file) => {
    setPreviewLoading(true);
    setPreviewError(false);
    
    try {
      console.log('Previewing file:', file);
      
      // Cleanup previous blob URL
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
      
      if (isImage(file.fileName) || isPDF(file.fileName)) {
        // Fetch file as blob (similar to ContractPDFViewer pattern)
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5076/api';
        const previewUrl = `${baseUrl}/contract-access/${token}/download-file?path=${encodeURIComponent(file.filePath)}`;
        
        console.log('Preview URL:', previewUrl);
        
        const response = await fetch(previewUrl, {
          method: 'GET',
          headers: {
            'Accept': isImage(file.fileName) ? 'image/*' : 'application/pdf',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        
        setBlobUrl(url);
        setPreviewFile({
          ...file,
          url: url,
          type: isImage(file.fileName) ? 'image' : 'pdf'
        });
        
        // Reset preview controls
        setZoom(100);
        setRotation(0);
      }
    } catch (err) {
      console.error('Error previewing file:', err);
      setPreviewError(true);
      toast.error('Eroare la previzualizarea fișierului');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    // Cleanup blob URL
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    setPreviewFile(null);
    setPreviewError(false);
    setZoom(100);
    setRotation(0);
    setIsFullscreen(false);
  };

  // Preview controls (similar to ContractPDFViewer)
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 300));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 25));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleReset = () => {
    setZoom(100);
    setRotation(0);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <span className="ml-3 text-gray-600">Se încarcă documentele...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3 text-red-600">
            <AlertCircle className="w-6 h-6" />
            <div>
              <p className="font-medium">Eroare</p>
              <p className="text-sm text-red-500">{error}</p>
            </div>
          </div>
          <button
            onClick={loadDocuments}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Încearcă din nou
          </button>
        </div>
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">Nu există documente încărcate încă</p>
            <p className="text-sm text-gray-500 mt-1">
              Documentele semnate vor apărea aici după încărcare
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                Documente Semnate Încărcate
              </h3>
            </div>
            <button
              onClick={loadDocuments}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Reîncarcă lista"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Total: {documents.length} fișier(e)
          </p>

          <div className="space-y-2">
            {documents.map((doc, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {/* Icon */}
                <div className="flex-shrink-0">
                  {getFileIcon(doc.fileName)}
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {doc.fileName}
                  </p>
                  <p className="text-sm text-gray-500">
                    {doc.fileExtension?.toUpperCase() || 'FILE'}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {canPreview(doc.fileName) && (
                    <button
                      onClick={() => handlePreview(doc)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Previzualizare"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Descarcă"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Preview Modal - Similar to ContractPDFViewer */}
      {previewFile && (
        <div className={`${isFullscreen ? 'fixed' : 'fixed'} inset-0 z-50 bg-gray-900`}>
          {/* Modal Header - Similar to ContractPDFViewer */}
          <div className="flex items-center justify-between p-4 bg-white border-b">
            <div className="flex items-center space-x-4">
              <div className="flex items-center gap-2">
                {getFileIcon(previewFile.fileName)}
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {previewFile.fileName}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {previewFile.fileExtension?.toUpperCase()}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Zoom Controls */}
              <div className="flex items-center space-x-1 border rounded-lg p-1">
                <button
                  onClick={handleZoomOut}
                  className="p-1 rounded hover:bg-gray-100 transition-colors"
                  title="Micșorează"
                  disabled={zoom <= 25}
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="px-2 text-sm font-medium min-w-[3rem] text-center">
                  {zoom}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="p-1 rounded hover:bg-gray-100 transition-colors"
                  title="Mărește"
                  disabled={zoom >= 300}
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              </div>

              {/* Rotate */}
              <button
                onClick={handleRotate}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Rotește"
              >
                <RotateCw className="w-4 h-4" />
              </button>

              {/* Reset */}
              <button
                onClick={handleReset}
                className="px-3 py-1 text-sm border rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reset
              </button>

              {/* Download */}
              <button
                onClick={() => handleDownload(previewFile)}
                className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                title="Descarcă"
              >
                <Download className="w-4 h-4 mr-2" />
                Descarcă
              </button>

              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title={isFullscreen ? "Ieși din fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? (
                  <Minimize2 className="w-4 h-4" />
                ) : (
                  <Maximize2 className="w-4 h-4" />
                )}
              </button>

              {/* Close */}
              <button
                onClick={closePreview}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Închide"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Modal Content */}
          <div 
            className="overflow-hidden relative bg-gray-800"
            style={{ height: 'calc(100vh - 73px)' }}
          >
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-white">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                  <p>Se încarcă fișierul...</p>
                </div>
              </div>
            ) : previewError ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-white max-w-md px-4">
                  <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <X className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Eroare la încărcarea fișierului</h3>
                  <p className="text-gray-300 mb-4">
                    Nu s-a putut încărca fișierul pentru vizualizare. Poți să îl descarci direct.
                  </p>
                  <button
                    onClick={() => handleDownload(previewFile)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 inline-flex items-center transition-colors"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Descarcă
                  </button>
                </div>
              </div>
            ) : blobUrl ? (
              <div 
                className="w-full h-full flex items-center justify-center"
                style={{
                  transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                  transition: 'transform 0.2s ease'
                }}
              >
                {previewFile.type === 'image' ? (
                  <img
                    src={blobUrl}
                    alt={previewFile.fileName}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : previewFile.type === 'pdf' ? (
                  <embed
                    src={blobUrl}
                    type="application/pdf"
                    className="w-full h-full border-0 bg-white shadow-lg"
                    style={{ 
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                  />
                ) : null}
              </div>
            ) : null}
          </div>

          {/* Status Bar - Similar to ContractPDFViewer */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-t text-sm text-gray-600">
            <div className="flex items-center space-x-4">
              <span>Zoom: {zoom}%</span>
              <span>Rotație: {rotation}°</span>
              {previewLoading && (
                <span className="text-blue-600 font-medium">
                  ⏳ Se încarcă...
                </span>
              )}
              {previewError && (
                <span className="text-red-600 font-medium">
                  ⚠ Eroare la încărcare
                </span>
              )}
              {blobUrl && !previewLoading && !previewError && (
                <span className="text-green-600 font-medium">
                  ✓ Fișier încărcat
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-xs">
                {previewError 
                  ? 'Folosește butonul Descarcă pentru a salva fișierul' 
                  : 'Folosește controalele pentru a naviga'
                }
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadedDocumentsViewer;